import {
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import "./CoverageTable.css";

type Props = {
  area: any;
};

interface LocationData {
  code: string;
  city: string;
  stateOrProvince: string;
  county: string;
  country: string;
}

interface SelectionState {
  selectedState: string | null;
  selectedCounty: string | null;
  selectedCity: string | null;
}

interface SearchState {
  searchTerm: string;
}

const CoverageTable = ({ area }: Props) => {
  const [selection, setSelection] = useState<SelectionState>({
    selectedState: null,
    selectedCounty: null,
    selectedCity: null,
  });

  const [searchState, setSearchState] = useState<SearchState>({
    searchTerm: "",
  });

  // Check if country is USA to determine if county column should be shown
  const isUSACountry = useMemo(() => {
    if (!area?.zipCodes || !Array.isArray(area?.zipCodes)) return false;
    return area.zipCodes.some((item: LocationData) => item.country === "USA");
  }, [area?.zipCodes]);

  const groupedData = useMemo(() => {
    if (!area?.zipCodes || !Array.isArray(area?.zipCodes)) return null;

    let zipCodes = area.zipCodes;

    if (searchState.searchTerm.trim()) {
      const searchLower = searchState.searchTerm.toLowerCase();
      zipCodes = area.zipCodes.filter((item: LocationData) => {
        const state = (item.stateOrProvince || "").toLowerCase();
        const county = (item.county || "").toLowerCase();
        const city = (item.city || "").toLowerCase();
        const code = (item.code || "").toLowerCase();
        return (
          state.includes(searchLower) ||
          county.includes(searchLower) ||
          city.includes(searchLower) ||
          code.includes(searchLower)
        );
      });
    }

    const stateGroups = new Map<string, LocationData[]>();
    zipCodes.forEach((item: LocationData) => {
      if (!stateGroups.has(item.stateOrProvince)) {
        stateGroups.set(item.stateOrProvince, []);
      }
      stateGroups.get(item.stateOrProvince)!.push(item);
    });

    const countyGroups = new Map<string, LocationData[]>();
    if (isUSACountry) {
      zipCodes.forEach((item: LocationData) => {
        if (!countyGroups.has(item.county)) {
          countyGroups.set(item.county, []);
        }
        countyGroups.get(item.county)!.push(item);
      });
    }

    const cityGroups = new Map<string, LocationData[]>();
    zipCodes.forEach((item: LocationData) => {
      const cityKey = `${item.county}-${item.city}`;
      if (!cityGroups.has(cityKey)) {
        cityGroups.set(cityKey, []);
      }
      cityGroups.get(cityKey)!.push(item);
    });

    return {
      states: Array.from(stateGroups.entries()).map(([state, data]) => ({
        name: state,
        count: data.length,
      })),
      counties: isUSACountry
        ? Array.from(countyGroups.entries()).map(([county, data]) => ({
            name: county,
            count: data.length,
          }))
        : [],
      cities: Array.from(cityGroups.entries()).map(([cityKey, data]) => ({
        name: data[0].city,
        county: data[0].county,
        count: data.length,
      })),
    };
  }, [area?.zipCodes, isUSACountry, searchState.searchTerm]);

  useEffect(() => {
    if (groupedData && groupedData.states.length > 0) {
      const firstState = groupedData.states[0];

      if (
        !searchState.searchTerm.trim() ||
        !selection.selectedState ||
        !groupedData.states.find((s) => s.name === selection.selectedState)
      ) {
        if (isUSACountry) {
          const countiesInFirstState = area.zipCodes
            .filter(
              (item: LocationData) => item.stateOrProvince === firstState.name
            )
            .map((item: LocationData) => item.county)
            .filter(
              (county: string, index: number, arr: string[]) =>
                arr.indexOf(county) === index
            );

          const citiesInFirstCounty = area.zipCodes
            .filter(
              (item: LocationData) => item.county === countiesInFirstState[0]
            )
            .map((item: LocationData) => item.city)
            .filter(
              (city: string, index: number, arr: string[]) =>
                arr.indexOf(city) === index
            );

          setSelection({
            selectedState: firstState.name,
            selectedCounty:
              countiesInFirstState.length > 0 ? countiesInFirstState[0] : null,
            selectedCity:
              citiesInFirstCounty.length > 0 ? citiesInFirstCounty[0] : null,
          });
        } else {
          const citiesInFirstState = area.zipCodes
            .filter(
              (item: LocationData) => item.stateOrProvince === firstState.name
            )
            .map((item: LocationData) => item.city)
            .filter(
              (city: string, index: number, arr: string[]) =>
                arr.indexOf(city) === index
            );

          setSelection({
            selectedState: firstState.name,
            selectedCounty: null,
            selectedCity:
              citiesInFirstState.length > 0 ? citiesInFirstState[0] : null,
          });
        }
      }
    }
  }, [groupedData, area?.zipCodes, isUSACountry, searchState.searchTerm]); // eslint-disable-line

  const handleStateSelect = (state: string) => {
    if (isUSACountry) {
      setSelection({
        selectedState: state,
        selectedCounty: null,
        selectedCity: null,
      });
    } else {
      const citiesInState = area.zipCodes
        .filter((item: LocationData) => item.stateOrProvince === state)
        .map((item: LocationData) => item.city)
        .filter(
          (city: string, index: number, arr: string[]) =>
            arr.indexOf(city) === index
        );

      setSelection({
        selectedState: state,
        selectedCounty: null,
        selectedCity: citiesInState.length > 0 ? citiesInState[0] : null,
      });
    }
  };

  const handleCitySelect = (city: string) => {
    setSelection({
      ...selection,
      selectedCity: city,
    });
  };

  const getFilteredCounties = () => {
    if (!selection.selectedState || !groupedData || !isUSACountry) return [];

    const filteredZipCodes = area.zipCodes.filter(
      (item: LocationData) => item.stateOrProvince === selection.selectedState
    );

    const countyGroups = new Map<string, LocationData[]>();
    filteredZipCodes.forEach((item: LocationData) => {
      if (!countyGroups.has(item.county)) {
        countyGroups.set(item.county, []);
      }
      countyGroups.get(item.county)!.push(item);
    });

    return Array.from(countyGroups.entries()).map(([county, data]) => ({
      name: county,
      count: data.length,
    }));
  };

  const getFilteredCities = () => {
    if (!selection.selectedState || !groupedData) return [];

    let filteredZipCodes;
    if (isUSACountry) {
      filteredZipCodes = area.zipCodes.filter(
        (item: LocationData) =>
          item.stateOrProvince === selection.selectedState &&
          (!selection.selectedCounty ||
            item.county === selection.selectedCounty)
      );
    } else {
      filteredZipCodes = area.zipCodes.filter(
        (item: LocationData) => item.stateOrProvince === selection.selectedState
      );
    }

    const cityGroups = new Map<string, LocationData[]>();
    filteredZipCodes.forEach((item: LocationData) => {
      if (!cityGroups.has(item.city)) {
        cityGroups.set(item.city, []);
      }
      cityGroups.get(item.city)!.push(item);
    });

    return Array.from(cityGroups.entries()).map(([city, data]) => ({
      name: city,
      count: data.length,
    }));
  };

  const getFilteredZipCodes = () => {
    if (!selection.selectedCity) return [];

    return area.zipCodes.filter(
      (item: LocationData) => item.city === selection.selectedCity
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;
    setSearchState({
      searchTerm: searchValue,
    });

    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      const matchingItems = area.zipCodes.filter((item: LocationData) => {
        const state = (item.stateOrProvince || "").toLowerCase();
        const county = (item.county || "").toLowerCase();
        const city = (item.city || "").toLowerCase();
        const code = (item.code || "").toLowerCase();
        return (
          state.includes(searchLower) ||
          county.includes(searchLower) ||
          city.includes(searchLower) ||
          code.includes(searchLower)
        );
      });

      if (matchingItems.length > 0) {
        const firstMatch = matchingItems[0];
        setSelection({
          selectedState: firstMatch.stateOrProvince,
          selectedCounty: firstMatch.county,
          selectedCity: firstMatch.city,
        });
      }
    }
  };

  if (!groupedData) {
    return (
      <Box className="coverage-table-empty-state">
        <Typography className="coverage-table-empty-text">-</Typography>
      </Box>
    );
  }

  const gridTemplateColumns = isUSACountry ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "start",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontWeight: "500", fontSize: "16px" }}>
          {area?.country}
        </Typography>
        {area.zipCodes.length > 0 && (
          <TextField
            sx={{
              backgroundColor: "white",
              height: 40,
              width: "920px",
              maxWidth: "calc(100% - 120px) !important",
              ml: 2,
              mr: 1,
              mt: 0.5,
              borderRadius: 1,
              ".MuiOutlinedInput-root": { height: 40 },
            }}
            fullWidth
            id="search"
            variant="outlined"
            placeholder={
              area?.country === "USA"
                ? "Search by State, County, City or Zip Code"
                : "Search by Province, City or Postal Code"
            }
            value={searchState.searchTerm}
            onChange={handleSearchChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      </Box>
      {area.zipCodes.length > 0 && (
        <Box className="coverage-table-container">
          <Box className="coverage-table-body">
            <Box
              className="coverage-table-grid"
              style={{ gridTemplateColumns }}
            >
              <Box className="coverage-table-column">
                {groupedData.states.map((state, index) => (
                  <Box
                    key={state.name}
                    className={`coverage-table-row ${
                      selection.selectedState === state.name ? "selected" : ""
                    }`}
                    onClick={() => handleStateSelect(state.name)}
                  >
                    <Typography className="coverage-table-text">
                      {state.name}
                    </Typography>
                    <Chip
                      label={state.count}
                      size="small"
                      sx={{
                        backgroundColor: "transparent",
                        color: "#e35f00",
                        fontSize: "0.75rem",
                        height: "20px",
                        border: "1px solid #e35f00",
                        borderRadius: "10px",
                        padding: "0px",
                        "& .MuiChip-label": {
                          color: "#e35f00",
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {isUSACountry && (
                <Box className="coverage-table-column">
                  {getFilteredCounties().map((county, index) => (
                    <Box
                      key={county.name}
                      className={`coverage-table-row ${
                        selection.selectedCounty === county.name
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => {
                        const citiesInCounty = area.zipCodes
                          .filter(
                            (item: LocationData) => item.county === county.name
                          )
                          .map((item: LocationData) => item.city)
                          .filter(
                            (city: string, index: number, arr: string[]) =>
                              arr.indexOf(city) === index
                          );

                        setSelection({
                          ...selection,
                          selectedCounty: county.name,
                          selectedCity:
                            citiesInCounty.length > 0
                              ? citiesInCounty[0]
                              : null,
                        });
                      }}
                    >
                      <Typography className="coverage-table-text">
                        {county.name}
                      </Typography>
                      <Chip
                        label={county.count}
                        size="small"
                        sx={{
                          backgroundColor: "transparent",
                          color: "#e35f00",
                          fontSize: "0.75rem",
                          height: "20px",
                          border: "1px solid #e35f00",
                          borderRadius: "10px",
                          padding: "0px",
                          "& .MuiChip-label": {
                            color: "#e35f00",
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
              <Box className="coverage-table-column">
                {getFilteredCities().map((city, index) => (
                  <Box
                    key={city.name}
                    className={`coverage-table-row ${
                      selection.selectedCity === city.name ? "selected" : ""
                    }`}
                    onClick={() => handleCitySelect(city.name)}
                  >
                    <Typography className="coverage-table-text">
                      {city.name}
                    </Typography>
                    <Chip
                      label={city.count}
                      size="small"
                      sx={{
                        backgroundColor: "transparent",
                        color: "#e35f00",
                        fontSize: "0.75rem",
                        height: "20px",
                        border: "1px solid #e35f00",
                        borderRadius: "10px",
                        padding: "0px",
                        "& .MuiChip-label": {
                          color: "#e35f00",
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
              <Box className="coverage-table-column">
                {getFilteredZipCodes().map(
                  (zipCode: LocationData, index: number) => (
                    <Box
                      key={zipCode.code}
                      className="coverage-table-row zip-code"
                    >
                      <Typography className="coverage-table-text">
                        {zipCode.code}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CoverageTable;
